/* global globalThis */
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateActivity, expireSession } from "../../redux/slices/authSlice";
import { useNavigate } from "react-router-dom";

// 15 minutes in milliseconds (15 * 60 * 1000)
const IDLE_TIMEOUT = 15 * 60 * 1000;
// 12 hours in milliseconds (12 * 60 * 60 * 1000)
const MAX_SESSION_DURATION = 12 * 60 * 60 * 1000;
// Throttle interval for activity updates (30 seconds)
const THROTTLE_INTERVAL = 30 * 1000;
const ActivityTracker = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, lastActivity, sessionStart } = useSelector(
    (state) => state.auth,
  );
  const lastUpdateRef = useRef(Date.now());

  const updateUserActivity = () => {
    if (!isAuthenticated) return;

    const now = Date.now();
    // Throttle updates to avoid excessive localStorage writes and cross-tab syncing events
    if (now - lastUpdateRef.current > THROTTLE_INTERVAL) {
      lastUpdateRef.current = now;
      dispatch(updateActivity());
    }
  };

  // Set up event listeners for user activity
  useEffect(
    () => {
      if (!isAuthenticated) return;

      // Add event listeners for user activity
      const events = [
        "mousedown",
        "mousemove",
        "keydown",
        "scroll",
        "touchstart",
        "click",
      ];
      for (const event of events) {
        globalThis.addEventListener(event, updateUserActivity);
      }
      // Listen for localStorage changes from other tabs
      const handleStorageChange = (e) => {
        if (e.key === "lastActivity" && e.newValue) {
          const newActivityTime = Number.parseInt(e.newValue, 10);
          // Only update if the new timestamp is fresher than our local state
          // to avoid infinite sync loops (Ping-Pong)
          if (newActivityTime > lastActivity) {
            dispatch(
              updateActivity({ timestamp: newActivityTime, skipStorage: true }),
            );
            lastUpdateRef.current = newActivityTime;
          }
        } else if (e.key === "sessionExpired" && e.newValue === "true") {
          const isIdleTimeout =
            localStorage.getItem("isIdleTimeout") === "true";
          dispatch(expireSession({ isIdleTimeout }));
          navigate("/session-timeout", { replace: true });
        } else if (e.key === "sessionExpired" && e.newValue === null) {
          // Handle case where sessionExpired was cleared (logout)
          checkSessionTimeout();
        }
      };
      globalThis.addEventListener("storage", handleStorageChange);

      // Sync when tab becomes active/focused
      const handleVisibilityChange = () => {
        if (!document.hidden && isAuthenticated) {
          // When tab becomes visible, sync with latest localStorage data
          const storedLastActivity = Number.parseInt(
            localStorage.getItem("lastActivity") || "0",
            10,
          );
          if (storedLastActivity > lastActivity) {
            dispatch(
              updateActivity({
                timestamp: storedLastActivity,
                skipStorage: true,
              }),
            );
            lastUpdateRef.current = storedLastActivity;
          }
          // Also check for session expiration immediately
          checkSessionTimeout();

          // Additional check: if we've been inactive for a while, force an immediate timeout check
          const timeSinceLastCheck = Date.now() - lastUpdateRef.current;
          if (timeSinceLastCheck > THROTTLE_INTERVAL) {
            // Force immediate check if we've been inactive longer than throttle interval
            setTimeout(checkSessionTimeout, 100);
          }
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      globalThis.addEventListener("focus", handleVisibilityChange);

      const checkSessionTimeout = () => {
        if (!isAuthenticated) return;

        const now = Date.now();
        // Read fresh data from localStorage to ensure we are synced even if events were missed
        const freshLastActivity = Number.parseInt(
          localStorage.getItem("lastActivity") || "0",
          10,
        );
        const freshSessionStart = Number.parseInt(
          localStorage.getItem("sessionStart") || "0",
          10,
        );
        const sessionExpired =
          localStorage.getItem("sessionExpired") === "true";

        // Check for fresh navigation scenario - if we have valid token but stale sessionExpired flag
        const token = localStorage.getItem("token");
        if (sessionExpired && token && freshSessionStart) {
          const sessionAge = now - freshSessionStart;
          const maxSessionDuration = 12 * 60 * 60 * 1000; // 12 hours
          const lastActivityAge =
            now - Math.max(freshLastActivity, lastActivity || 0);
          const idleTimeout = 15 * 60 * 1000; // 15 minutes

          // If session is not actually expired based on age and activity, clear the expired flag
          if (
            sessionAge < maxSessionDuration &&
            lastActivityAge < idleTimeout
          ) {
            localStorage.removeItem("sessionExpired");
            localStorage.removeItem("isIdleTimeout");
            return; // Don't redirect, allow normal navigation
          }
        }

        // If session is already expired in localStorage, handle it immediately
        if (
          sessionExpired &&
          globalThis.location.pathname !== "/session-timeout"
        ) {
          const isIdleTimeout =
            localStorage.getItem("isIdleTimeout") === "true";
          dispatch(expireSession({ isIdleTimeout }));
          navigate("/session-timeout", { replace: true });
          return;
        }

        const timeSinceLastActivity =
          now - Math.max(freshLastActivity, lastActivity || 0);
        const sessionDuration =
          now - Math.max(freshSessionStart, sessionStart || 0);

        const isIdleTimeout = timeSinceLastActivity >= IDLE_TIMEOUT;
        const isMaxSessionReached = sessionDuration >= MAX_SESSION_DURATION;

        if (isIdleTimeout || isMaxSessionReached) {
          // Only trigger if not already showing expired state (to avoid multiple history pushes)
          if (globalThis.location.pathname !== "/session-timeout") {
            // Set session expiration in localStorage so other tabs can detect it
            localStorage.setItem("sessionExpired", "true");
            localStorage.setItem(
              "isIdleTimeout",
              isIdleTimeout ? "true" : "false",
            );
            navigate("/session-timeout", { replace: true });
            dispatch(expireSession({ isIdleTimeout }));
          }
        }
      };

      // Check every 30 seconds for more responsive cross-tab sync
      const intervalId = setInterval(checkSessionTimeout, 30000);

      // Initial check
      checkSessionTimeout();

      // Cleanup
      return () => {
        for (const event of events) {
          globalThis.removeEventListener(event, updateUserActivity);
        }
        globalThis.removeEventListener("storage", handleStorageChange);
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
        globalThis.removeEventListener("focus", handleVisibilityChange);
        clearInterval(intervalId);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated, lastActivity, sessionStart, dispatch, navigate],
  );

  return null; // This component doesn't render anything
};

export default ActivityTracker;
